"use client";

import { type PostContactSubmissionMutationVariables } from "~/__generated__/graphql";
import Select from "~/components/forms/Elements/Select";
import { FormProvider, useForm } from "react-hook-form";
import { cn } from "~/utils/cn";
import { useEffect, useRef } from "react";
import { Subjects, Warehouses } from "~/types/contact";
import ReCAPTCHA from "react-google-recaptcha";
import { env } from "~/env.mjs";

export default function ContactForm({
  defaultSubject,
  defaultWarehouse,
  humanResourcesCountries,
}: {
  defaultSubject: string;
  defaultWarehouse: string;
  humanResourcesCountries: string[];
}) {
  const captchaField = useRef<ReCAPTCHA>(null);

  const methods = useForm<PostContactSubmissionMutationVariables>({
    mode: "onBlur",
  });

  const {
    watch,
    register,
    unregister,
    handleSubmit,
    setError,
    control,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const captcha = await captchaField.current?.executeAsync();
      const response = await fetch("/api/submitContactForm", {
        method: "POST",
        body: JSON.stringify({ data, captcha }),
      });

      const res = (await response.json()) as {
        message: string;
      };

      if (response.status !== 200) {
        setError("root.serverError", {
          type: response.status.toString(),
          message: res.message,
        });
      }
    } catch (e) {
      setError("root.serverError", {
        type: "500",
        message: "Something went wrong!",
      });
    }
  });

  const subjectWatch = watch("subject");

  // Clear values that are not in use so they don't get submitted
  useEffect(() => {
    if (subjectWatch === "New Customer") register("region");
    else unregister("region");
    if (subjectWatch === "Warehouse") register("warehouse");
    else unregister("warehouse");
    if (subjectWatch === "Human Resources") register("country");
    else unregister("country");
  }, [register, subjectWatch, unregister]);

  return (
    <div className="flex-1">
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className="flex flex-col gap-8">
          <ReCAPTCHA
            sitekey={env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={captchaField}
          />

          <div className="flex flex-col justify-between gap-7 lg:flex-row">
            <input
              {...register("name")}
              placeholder="Your name*"
              required
              className="h-15 w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
            />
            <input
              {...register("email")}
              placeholder="Email*"
              type="email"
              required
              className="h-15 w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
            />
          </div>
          <div className="flex flex-col justify-between gap-7 lg:flex-row">
            <input
              {...register("phoneNumber")}
              placeholder="Phone Number*"
              type="tel"
              required
              className="h-15 w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
            />
            <input
              {...register("city")}
              placeholder="City*"
              required
              className="h-15 w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
            />
          </div>
          <div className="flex flex-col justify-between gap-7 lg:flex-row">
            <Select
              name="subject"
              placeholder="Subject"
              defaultValue={defaultSubject}
              control={control}
              required
              options={Subjects}
            />
            {subjectWatch === "New Customer" && (
              <div className=" w-full">
                <input
                  {...register("region")}
                  placeholder="Region/country of interest*"
                  required
                  className="h-15 w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
                />
              </div>
            )}
            {subjectWatch === "Warehouse" && (
              <Select
                name="warehouse"
                placeholder="Warehouse location"
                defaultValue={defaultWarehouse}
                control={control}
                required
                options={Warehouses}
              />
            )}
            {subjectWatch === "Human Resources" && (
              <Select
                name="country"
                placeholder="Country"
                control={control}
                required
                options={humanResourcesCountries}
              />
            )}
          </div>
          <textarea
            {...register("message")}
            placeholder="Your message*"
            rows={5}
            required
            className="w-full rounded-sm border border-white bg-black px-7 py-3 text-lg font-normal leading-relaxed text-white placeholder-white focus-visible:outline-none"
          />

          <button
            type="submit"
            disabled={isSubmitting || isSubmitSuccessful}
            className={cn(
              "min-h-[3.5rem] rounded-sm bg-blue px-6 py-1 text-lg font-normal tracking-wide text-white",
              (isSubmitting || isSubmitSuccessful) && "opacity-50",
            )}
          >
            {isSubmitted &&
              !isSubmitSuccessful &&
              errors.root?.serverError &&
              "Something went wrong! Please try again later."}
            {isSubmitting && !errors.root?.serverError && "Submitting..."}
            {!isSubmitted &&
              !isSubmitting &&
              !errors.root?.serverError &&
              "Submit Message"}
            {isSubmitted &&
              isSubmitSuccessful &&
              !errors.root?.serverError &&
              "Submitted!"}
          </button>
        </form>
      </FormProvider>
    </div>
  );
}
